import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import {
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from "@material-ui/core";
import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const StatCard = ({ label, value }) => (
  <Paper variant="outlined" style={{ padding: 16, height: "100%" }}>
    <Typography variant="body2" color="textSecondary">
      {label}
    </Typography>
    <Typography variant="h5">{value}</Typography>
  </Paper>
);

const formatDateValue = value => {
  if (!value) {
    return "-";
  }

  return new Date(value).toLocaleDateString("pt-BR");
};

const formatCurrencyValue = (value, currency = "BRL") => {
  if (value === null || value === undefined || value === "") {
    return "-";
  }

  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: currency || "BRL"
  }).format(Number(value));
};

const emptyBillingForm = currency => ({
  type: "payment",
  amount: "",
  currency: currency || "BRL",
  paidAt: new Date().toISOString().slice(0, 10),
  dueAt: "",
  coverageStartsAt: "",
  coverageEndsAt: "",
  notes: ""
});

const CompanyDetailsDialog = ({
  open,
  onClose,
  companyId,
  onArchiveCompany,
  onDeleteCompany,
  actionLoading = false
}) => {
  const [company, setCompany] = useState(null);
  const [tempPassword, setTempPassword] = useState(null);
  const [billingForm, setBillingForm] = useState(emptyBillingForm("BRL"));
  const [ticketSubjectRequired, setTicketSubjectRequired] = useState(false);
  const [savingSubjectSetting, setSavingSubjectSetting] = useState(false);
  const { startSupportSession } = useContext(AuthContext);
  const supportTransitionRef = useRef(false);

  const fetchCompany = useCallback(async () => {
    if (!open || !companyId) {
      return;
    }

    try {
      const { data } = await api.get(`/companies/${companyId}`);
      setCompany(data);
      setBillingForm(current => ({
        ...emptyBillingForm(data.currency || "BRL"),
        ...current,
        currency: current.currency || data.currency || "BRL"
      }));
    } catch (err) {
      const errorMsg = err?.response?.data?.error || err?.response?.data?.message;
      if (supportTransitionRef.current && errorMsg === "ERR_NO_PERMISSION") {
        return;
      }
      toastError(err);
    }
  }, [companyId, open]);

  useEffect(() => {
    fetchCompany();
  }, [fetchCompany]);

  useEffect(() => {
    if (!open) {
      setCompany(null);
      setTempPassword(null);
      setBillingForm(emptyBillingForm("BRL"));
    }
  }, [open]);

  const handleResetAdminPassword = async () => {
    try {
      const { data } = await api.post(
        `/companies/${companyId}/reset-admin-password`
      );
      setTempPassword(data);
      toast.success("Senha temporária gerada.");
      await fetchCompany();
    } catch (err) {
      toastError(err);
    }
  };

  const handleStartSupport = async () => {
    supportTransitionRef.current = true;
    onClose();
    await startSupportSession(companyId);
  };

  const handleBillingFieldChange = ({ target: { name, value } }) => {
    setBillingForm(current => ({
      ...current,
      [name]: value
    }));
  };

  const handleAddBillingEvent = async () => {
    try {
      await api.post(`/companies/${companyId}/billing-events`, {
        type: billingForm.type,
        amount: billingForm.amount === "" ? null : Number(billingForm.amount),
        currency: billingForm.currency,
        paidAt: billingForm.paidAt || null,
        dueAt: billingForm.dueAt || null,
        coverageStartsAt: billingForm.coverageStartsAt || null,
        coverageEndsAt: billingForm.coverageEndsAt || null,
        notes: billingForm.notes || null
      });

      toast.success("Evento financeiro registrado.");
      setBillingForm(emptyBillingForm(company?.currency || "BRL"));
      await fetchCompany();
    } catch (err) {
      toastError(err);
    }
  };

  const handleSaveTicketSubjectRequired = async () => {
    if (!company) {
      return;
    }

    setSavingSubjectSetting(true);
    try {
      const { data } = await api.put(`/companies/${company.id}`, {
        ticketSubjectRequired
      });
      setCompany(data);
      setTicketSubjectRequired(Boolean(data.ticketSubjectRequired));
      toast.success("Configuração da empresa atualizada.");
    } catch (err) {
      toastError(err);
    } finally {
      setSavingSubjectSetting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle>Detalhes da empresa</DialogTitle>
      <DialogContent dividers>
        {company ? (
          <>
            <Typography variant="h5" gutterBottom>
              {company.name}
            </Typography>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Slug: {company.slug}
            </Typography>
            <Typography variant="body2" gutterBottom>
              Plano: <strong>{company.plan}</strong>
            </Typography>
            <Typography variant="body2" gutterBottom>
              Mensalidade:{" "}
              <strong>
                {formatCurrencyValue(company.monthlyPrice, company.currency)}
              </strong>
            </Typography>
            <Typography variant="body2" gutterBottom>
              Status comercial: <strong>{company.subscriptionStatus}</strong>
            </Typography>
            <Typography variant="body2" gutterBottom>
              Admin principal:{" "}
              <strong>{company.primaryAdmin?.email || "Sem admin principal"}</strong>
            </Typography>
            <Typography variant="body2" gutterBottom>
              SIP da empresa:{" "}
              <strong>{company.sipEnabled ? "Habilitado" : "Desabilitado"}</strong>
            </Typography>
            <Typography variant="body2" gutterBottom>
              Dominio SIP: <strong>{company.sipDomain || "-"}</strong>
            </Typography>
            <Typography variant="body2" gutterBottom>
              Transporte SIP: <strong>{(company.sipTransport || "-").toUpperCase()}</strong>
            </Typography>
            <Typography variant="body2" gutterBottom>
              Media address SIP: <strong>{company.sipMediaAddress || "-"}</strong>
            </Typography>
            <Typography variant="body2" gutterBottom>
              Limite de ramais: <strong>{company.maxSipExtensions || "Ilimitado"}</strong>
            </Typography>

            <Typography variant="body2" gutterBottom>
              Fim do trial: <strong>{formatDateValue(company.trialEndsAt)}</strong>
            </Typography>
            <Typography variant="body2" gutterBottom>
              Vencimento: <strong>{formatDateValue(company.subscriptionEndsAt)}</strong>
            </Typography>
            <Typography variant="body2" gutterBottom>
              Último pagamento: <strong>{formatDateValue(company.lastPaymentAt)}</strong>
            </Typography>
            <Typography variant="body2" gutterBottom>
              Carência até: <strong>{formatDateValue(company.graceEndsAt)}</strong>
            </Typography>
            <Typography variant="body2" gutterBottom>
              Suspensão programada:{" "}
              <strong>{formatDateValue(company.scheduledSuspensionAt)}</strong>
            </Typography>
            <Typography variant="body2" gutterBottom>
              Status:{" "}
              <Chip
                size="small"
                label={
                  company.archivedAt
                    ? "Arquivada"
                    : company.status === "active"
                    ? "Ativa"
                    : "Inativa"
                }
                color={
                  company.archivedAt
                    ? "secondary"
                    : company.status === "active"
                    ? "primary"
                    : "default"
                }
              />
            </Typography>
            <Typography variant="body2" gutterBottom>
              Arquivada em: <strong>{formatDateValue(company.archivedAt)}</strong>
            </Typography>
            <Typography variant="body2" gutterBottom>
              Arquivo ate: <strong>{formatDateValue(company.archiveExpiresAt)}</strong>
            </Typography>
            <Typography variant="body2" gutterBottom>
              Motivo: <strong>{company.statusReason || "-"}</strong>
            </Typography>
            <Typography variant="body2" gutterBottom>
              Observações internas: <strong>{company.internalNotes || "-"}</strong>
            </Typography>

            <div style={{ marginTop: 12, marginBottom: 16 }}>
              {false && (
              <Paper
                variant="outlined"
                style={{ padding: 16, marginBottom: 16, background: "#fafafa" }}
              >
                <Typography variant="subtitle2" gutterBottom>
                  Operação do chamado
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      color="primary"
                      checked={ticketSubjectRequired}
                      onChange={(event) =>
                        setTicketSubjectRequired(event.target.checked)
                      }
                    />
                  }
                  label="Assunto chamado obrigatório"
                />
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Quando ativado, o encerramento do ticket exige assunto e um breve resumo.
                </Typography>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleSaveTicketSubjectRequired}
                  disabled={savingSubjectSetting || actionLoading}
                >
                  Salvar regra
                </Button>
              </Paper>
              )}
              <Button
                variant="contained"
                color="primary"
                onClick={handleStartSupport}
                style={{ marginRight: 8 }}
                disabled={Boolean(company.archivedAt) || actionLoading}
              >
                Entrar como admin da empresa
              </Button>
              <Button
                variant="outlined"
                color="primary"
                onClick={handleResetAdminPassword}
                disabled={actionLoading}
                style={{ marginRight: 8 }}
              >
                Gerar senha provisória do admin
              </Button>
              {!company.archivedAt ? (
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={() => onArchiveCompany && onArchiveCompany(company)}
                  disabled={actionLoading}
                  style={{ marginRight: 8 }}
                >
                  Arquivar empresa
                </Button>
              ) : null}
              <Button
                variant="contained"
                color="secondary"
                onClick={() => onDeleteCompany && onDeleteCompany(company)}
                disabled={actionLoading}
              >
                Excluir empresa
              </Button>
            </div>

            {tempPassword ? (
              <Paper
                variant="outlined"
                style={{ padding: 16, marginBottom: 16, background: "#fafafa" }}
              >
                <Typography variant="subtitle2" gutterBottom>
                  Senha provisória gerada
                </Typography>
                <Typography variant="body2">
                  Admin: <strong>{tempPassword.adminUser?.email}</strong>
                </Typography>
                <Typography variant="body2">
                  Senha: <strong>{tempPassword.tempPassword}</strong>
                </Typography>
              </Paper>
            ) : null}

            <Grid container spacing={2} style={{ marginTop: 8, marginBottom: 16 }}>
              <Grid item xs={12} sm={6} md={4}>
                <StatCard
                  label="Usuários"
                  value={`${company.metrics?.usersCount || 0} / ${
                    company.maxUsers || "Ilimitado"
                  }`}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <StatCard
                  label="Filas"
                  value={`${company.metrics?.queuesCount || 0} / ${
                    company.maxQueues || "Ilimitado"
                  }`}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <StatCard
                  label="Conexões"
                  value={`${company.metrics?.whatsappsCount || 0} / ${
                    company.maxWhatsapps || "Ilimitado"
                  }`}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <StatCard
                  label="Ramais SIP"
                  value={`${company.metrics?.sipExtensionsCount || 0} / ${
                    company.maxSipExtensions || "Ilimitado"
                  }`}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <StatCard label="Admins" value={company.metrics?.adminsCount || 0} />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <StatCard
                  label="Contatos"
                  value={company.metrics?.contactsCount || 0}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <StatCard
                  label="Tickets abertos"
                  value={company.metrics?.openTicketsCount || 0}
                />
              </Grid>
            </Grid>

            <Typography variant="h6" gutterBottom>
              Financeiro manual
            </Typography>

            <Paper variant="outlined" style={{ padding: 16, marginBottom: 24 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl variant="outlined" size="small" fullWidth>
                    <InputLabel>Tipo</InputLabel>
                    <Select
                      label="Tipo"
                      name="type"
                      value={billingForm.type}
                      onChange={handleBillingFieldChange}
                    >
                      <MenuItem value="payment">Pagamento</MenuItem>
                      <MenuItem value="renewal">Renovação</MenuItem>
                      <MenuItem value="trial_extension">Extensão de trial</MenuItem>
                      <MenuItem value="grace_extension">Extensão de carência</MenuItem>
                      <MenuItem value="adjustment">Ajuste</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    variant="outlined"
                    size="small"
                    label="Valor"
                    name="amount"
                    type="number"
                    fullWidth
                    inputProps={{ step: "0.01", min: "0" }}
                    value={billingForm.amount}
                    onChange={handleBillingFieldChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <FormControl variant="outlined" size="small" fullWidth>
                    <InputLabel>Moeda</InputLabel>
                    <Select
                      label="Moeda"
                      name="currency"
                      value={billingForm.currency}
                      onChange={handleBillingFieldChange}
                    >
                      <MenuItem value="BRL">BRL</MenuItem>
                      <MenuItem value="USD">USD</MenuItem>
                      <MenuItem value="EUR">EUR</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    variant="outlined"
                    size="small"
                    label="Pago em"
                    name="paidAt"
                    type="date"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    value={billingForm.paidAt}
                    onChange={handleBillingFieldChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    variant="outlined"
                    size="small"
                    label="Vencimento"
                    name="dueAt"
                    type="date"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    value={billingForm.dueAt}
                    onChange={handleBillingFieldChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    variant="outlined"
                    size="small"
                    label="Cobertura inicial"
                    name="coverageStartsAt"
                    type="date"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    value={billingForm.coverageStartsAt}
                    onChange={handleBillingFieldChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    variant="outlined"
                    size="small"
                    label="Cobertura final"
                    name="coverageEndsAt"
                    type="date"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    value={billingForm.coverageEndsAt}
                    onChange={handleBillingFieldChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    variant="outlined"
                    size="small"
                    label="Observações"
                    name="notes"
                    fullWidth
                    multiline
                    minRows={2}
                    value={billingForm.notes}
                    onChange={handleBillingFieldChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleAddBillingEvent}
                  >
                    Registrar evento financeiro
                  </Button>
                </Grid>
              </Grid>
            </Paper>

            <Typography variant="h6" gutterBottom>
              Histórico financeiro
            </Typography>

            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Quando</TableCell>
                  <TableCell>Tipo</TableCell>
                  <TableCell>Valor</TableCell>
                  <TableCell>Cobertura</TableCell>
                  <TableCell>Observações</TableCell>
                  <TableCell>Responsável</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(company.billingEvents || []).map(event => (
                  <TableRow key={event.id}>
                    <TableCell>
                      {new Date(event.createdAt).toLocaleString("pt-BR")}
                    </TableCell>
                    <TableCell>{event.type}</TableCell>
                    <TableCell>
                      {formatCurrencyValue(event.amount, event.currency)}
                    </TableCell>
                    <TableCell>
                      {formatDateValue(event.coverageStartsAt)} até{" "}
                      {formatDateValue(event.coverageEndsAt)}
                    </TableCell>
                    <TableCell>{event.notes || "-"}</TableCell>
                    <TableCell>{event.actor?.email || "Sistema"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <Typography variant="h6" gutterBottom style={{ marginTop: 24 }}>
              Usuários da empresa
            </Typography>

            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Nome</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Perfil</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(company.users || []).map(user => (
                  <TableRow key={user.id}>
                    <TableCell>{user.name}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{user.profile}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <Typography variant="h6" gutterBottom style={{ marginTop: 24 }}>
              Linha do tempo
            </Typography>

            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Quando</TableCell>
                  <TableCell>Categoria</TableCell>
                  <TableCell>Evento</TableCell>
                  <TableCell>Detalhes</TableCell>
                  <TableCell>Responsável</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(company.events || []).map(event => (
                  <TableRow key={event.id}>
                    <TableCell>
                      {new Date(event.createdAt).toLocaleString("pt-BR")}
                    </TableCell>
                    <TableCell>{event.category}</TableCell>
                    <TableCell>{event.title}</TableCell>
                    <TableCell>{event.description || "-"}</TableCell>
                    <TableCell>{event.actor?.email || "Sistema"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        ) : (
          <Typography>Carregando...</Typography>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CompanyDetailsDialog;
